import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, ScrollView, Platform } from 'react-native';
import { interpolateBlues } from 'd3-scale-chromatic';
import { schemeCategory10 } from 'd3-scale-chromatic';
import { scaleOrdinal } from 'd3-scale';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { findImmediateParent, getAllItems, getItemById } from '../common/service/dataOperations';
import processData from '../common/service/dataProcessor';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import CustomDT from '../common/controls/customDataTable';
import { colorType3, colorType5, logStyles } from './LogStyles';
import { Loader } from '../../Common/Loader';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import GeoMap from '../common/controls/GeoMap';
import { useNav } from '../../../providers/NavProvider';
import LogKpiRow from './LogKpiRow';
import LogTypeBreakdown from './LogTypeBreakdown';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';
import { buildPieData, buildLineData, buildPeriodTable } from '../common/controls/chartDataUtils';
import { useKpiConfig } from '../Dashboard/kpi/useKpiConfig';

interface Props {
  siteId: string;
  logTitle: string;
  Permissions?: string;
}

export default function ObservationLog(props: Props) {
  const [observationLogData, setObservationLogData] = useState<null | any>(null);
  const [observationLogColumns, setObservationLogColumns] = useState<null | any>(null);
  const [onlyForm, setonlyForm] = useState<boolean>(false);
  const { sitesResp } = useNav();
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();
  let itemID = undefined;
  const url = Platform.OS === 'web' ? new URLSearchParams(document.location.search) : null;
  itemID = url && url.get('ID');

  useEffect(() => {
    if (itemID && parseInt(itemID) > 0) {
      setonlyForm(true);
    }
  }, [itemID]);

  useEffect(() => {
    async function fetchData() {
      try {
        const packageSite = sitesResp && findImmediateParent(sitesResp, props.siteId);
        if (packageSite && packageSite.id) {
          const logTitle = props.logTitle;
          const accessToken = sitesResp && await getAccessToken();
          const json = sitesResp && await getAllItems(logTitle, accessToken, clientAPIURL, props.siteId, undefined, packageSite.id);
          setObservationLogData(json.items);
          setObservationLogColumns(json.columns);
        }
      } catch (error) {
        console.error(error);
      }
    }
    sitesResp && fetchData();
  }, [props.logTitle, sitesResp]);

  const memoizedCreateLogCharts = useMemo(() => (
    <CreateLogCharts
      itemID={itemID}
      FormOnly={onlyForm}
      Permissions={props.Permissions}
      setObservationLogData={setObservationLogData}
      observationLogData={observationLogData}
      observationLogColumns={observationLogColumns}
      siteId={props.siteId}
      logTitle={props.logTitle}
      clientAPIURL={clientAPIURL}
    />
  ), [itemID, onlyForm, props.Permissions, observationLogData, observationLogColumns, props.siteId, props.logTitle, clientAPIURL]);

  return observationLogData && observationLogColumns ? memoizedCreateLogCharts : <Loader />;
}

interface CreateLogChartsProps {
  observationLogData: any;
  observationLogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
  setObservationLogData: any;
  Permissions?: string;
  FormOnly: boolean;
  itemID: any;
}

const CreateLogCharts: React.FC<CreateLogChartsProps> = ({
  FormOnly,
  itemID,
  Permissions,
  observationLogData,
  observationLogColumns,
  siteId,
  logTitle,
  clientAPIURL,
}) => {
  const { getAccessToken } = useAuth();
  const { currentLeafObj } = useNav();
  const defaultTab = currentLeafObj.permissions && JSON.stringify(currentLeafObj.permissions).toLowerCase().includes('write') ? 'list' : 'chart';

  // No projectId is threaded into this component — modules.compliance isn't project-scoped
  // in practice today, and resolveKpiConfig degrades gracefully to pm.default when null.
  const [kpiAccessToken, setKpiAccessToken] = useState<string | null>(null);
  useEffect(() => { getAccessToken().then(setKpiAccessToken); }, []);
  const kpiConfig = useKpiConfig(clientAPIURL, kpiAccessToken, null);

  const ColumnsWithIDColumn = useMemo(() => [
    ...observationLogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      order: 0,
    },
  ], [observationLogColumns]);

  const [filteredData, setFilteredData] = useState<any[]>([]);
  useEffect(() => {
    setFilteredData(observationLogData ?? []);
  }, [observationLogData]);

  const [_showFilter, setShowFilter] = useState<boolean>(true);
  const [onlyForm, setonlyForm] = useState<boolean>(FormOnly);

  async function handleDataUpdate(item: any) {
    if (item.ID) {
      try {
        const accessToken = await getAccessToken();
        const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
        if (result.items[0]) {
          const [processedItem] = processData(logTitle, [result.items[0]]);
          setFilteredData(prev => [...prev.filter((o: any) => o.ID !== item.ID), processedItem]);
        }
      } catch (ex) {
        console.error(ex);
      }
    }
  }

  const recordsWithCoordinates = useMemo(() =>
    filteredData.filter(record => {
      const loc = record.Location;
      if (!loc || !loc.includes('coords')) return false;
      try {
        const location = JSON.parse(loc);
        return location?.coords?.latitude && location?.coords?.longitude;
      } catch {
        return false;
      }
    }),
  [filteredData]);

  const chartData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const parseDDMMYYYY = (s: string): Date => {
      const [d, m, y] = s.split('/');
      return new Date(+y, +m - 1, +d);
    };

    const comparePeriods = (a: string, b: string): number => {
      const [am, ay] = a.split('-');
      const [bm, by] = b.split('-');
      return (+ay - +by) || (+am - +bm);
    };

    const colorOrdinal = scaleOrdinal(schemeCategory10);
    const lineColorFn = (i: number): string => colorOrdinal(String(i));

    const sortedPeriods = [...new Set(filteredData.map((i: any) => i.Period).filter(Boolean))]
      .sort(comparePeriods) as string[];

    // Pie charts
    // PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string
    const statusPie = buildPieData(filteredData, 'Status', colorType5);
    const typePie = buildPieData(filteredData, 'ObservationType', colorType3);
    const categoryPie = buildPieData(filteredData, 'Category', colorType5);

    // Line charts
    // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=fn
    const statusLine = buildLineData(filteredData, 'Status', lineColorFn);
    const typeLine = buildLineData(filteredData, 'ObservationType', lineColorFn);
    const categoryLine = buildLineData(filteredData, 'Category', lineColorFn);

    // Summary tables
    const ObservationTypeChoices = observationLogColumns
      .find((col: any) => col.columnName === 'ObservationType')
      ?.choiceChoices?.split('#;#')
      .filter((v: string) => v !== 'undefined') ?? null;
    const CategoryChoices = observationLogColumns
      .find((col: any) => col.columnName === 'Category')
      ?.choiceChoices?.split('#;#')
      .filter((v: string) => v !== 'undefined') ?? null;

    const { tableData: tableByType, columnKeys: colsByType } = buildPeriodTable(
      filteredData, 'ObservationType', ObservationTypeChoices, 'Period', 'Status',
    );
    const { tableData: tableByCategory, columnKeys: colsByCategory } = buildPeriodTable(
      filteredData, 'Category', CategoryChoices, 'Period', 'Status',
    );

    // Overdue items (used by KPI row and overdue banner)
    const overdueItems = filteredData.filter(item => {
      if (!item.DueDate) return false;
      if (item.Status === 'Closed' || item.Status === 'Escalated') return false;
      return parseDDMMYYYY(item.DueDate) < today;
    });

    // Avg days to close — elapsed days between Date and DateClosedOut for closed items
    const closedWithBothDates = filteredData.filter((i: any) => i.Date && i.DateClosedOut);
    const avgDaysToClose = closedWithBothDates.length > 0
      ? Math.round(
          closedWithBothDates.reduce((sum: number, i: any) => {
            const days = (parseDDMMYYYY(i.DateClosedOut).getTime() - parseDDMMYYYY(i.Date).getTime()) / 86400000;
            return sum + Math.max(0, days);
          }, 0) / closedWithBothDates.length,
        )
      : 0;

    const kpiItems = [
      { label: 'Total',             value: filteredData.length,                                               color: '#1565C0' },
      { label: 'Open',              value: filteredData.filter((i: any) => i.Status === 'Open').length,       color: '#F57C00' },
      { label: 'Closed',            value: filteredData.filter((i: any) => i.Status === 'Closed').length,     color: '#2E7D32' },
      { label: 'Escalated',         value: filteredData.filter((i: any) => i.Status === 'Escalated').length,  color: '#C62828' },
      { label: 'Overdue',           value: overdueItems.length,                                               color: '#B71C1C' },
      ...(kpiConfig.modules.compliance ? [{ label: 'Avg Days to Close', value: avgDaysToClose, color: '#0277BD' }] : []),
    ];

    // Good Practice vs Issues Trend (LineChartScreen)
    const GOOD_PRACTICE_VALUE = 'Good Practice/ Strong Point';
    const goodPracticeByPeriod = sortedPeriods.map(period =>
      filteredData.filter((i: any) => i.Period === period && i.ObservationType === GOOD_PRACTICE_VALUE).length,
    );
    const issuesByPeriod = sortedPeriods.map(period =>
      filteredData.filter((i: any) => i.Period === period && i.ObservationType !== GOOD_PRACTICE_VALUE && i.ObservationType).length,
    );
    const gpTrendData = {
      labels: sortedPeriods,
      datasets: [
        { data: goodPracticeByPeriod, strokeWidth: 4, color: (_opacity = 1) => '#558B2F' },
        { data: issuesByPeriod,        strokeWidth: 4, color: (_opacity = 1) => '#C62828' },
      ],
      legend: [{ name: 'Good Practice' }, { name: 'Issues' }],
    };

    return {
      sortedPeriods,
      statusPie, typePie, categoryPie,
      statusLine, typeLine, categoryLine,
      tableByType, colsByType,
      tableByCategory, colsByCategory,
      kpiItems, overdueItems,
      gpTrendData,
    };
  }, [filteredData, observationLogColumns, kpiConfig.modules.compliance]);

  const {
    sortedPeriods,
    statusPie, typePie, categoryPie,
    statusLine, typeLine, categoryLine,
    tableByType, colsByType,
    tableByCategory, colsByCategory,
    kpiItems, overdueItems,
    gpTrendData,
  } = chartData;

  const chartVisibility = (val: boolean) => { setonlyForm(val); };

  return (
    <View style={logStyles.logRoot}>
      <TabsProvider defaultIndex={onlyForm === true ? 2 : defaultTab && defaultTab === 'list' ? 2 : 0}>
        <Tabs style={{ backgroundColor: '#fff', height: '100%' }} showLeadingSpace={true}>
          <TabScreen label="Charts" icon="chart-bar">
            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={{ zIndex: 200 }}>
                  <LogKpiRow items={kpiItems}>
                    <LogDateRangeFilter data={observationLogData ?? []} onFilterChange={setFilteredData} />
                  </LogKpiRow>
                </View>
                <LogTypeBreakdown data={filteredData} columns={observationLogColumns} />
                {recordsWithCoordinates.length > 0 && (
                  <View style={{ padding: 10, alignItems: 'center' }}>
                    <View style={[logStyles.cardTwo, { height: 350 }]}>
                      <GeoMap data={recordsWithCoordinates} />
                    </View>
                  </View>
                )}
                <View style={[logStyles.container, { flex: 0, flexGrow: 0 }]}>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={statusPie} colorPallate={interpolateBlues} subTitle="(cumulative)" ChartTitle="Observations / Status" />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={statusLine.legend} data={statusLine} label={sortedPeriods} subTitle="(monthly)" ChartTitle="Observations / Status" />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={typePie} colorPallate={interpolateBlues} subTitle="(cumulative)" ChartTitle="Observations / Type" />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={typeLine.legend} data={typeLine} label={sortedPeriods} subTitle="(monthly)" ChartTitle="Observations / Type" />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={categoryPie} colorPallate={interpolateBlues} subTitle="(cumulative)" ChartTitle="Observations / Category" />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={categoryLine.legend} data={categoryLine} label={sortedPeriods} subTitle="(monthly)" ChartTitle="Observations / Category" />
                  </View>
                  {filteredData.length > 0 && (
                    <View style={[logStyles.card, { backgroundColor: '' }]}>
                      <LineChartScreen
                        legend={gpTrendData.legend}
                        data={gpTrendData}
                        label={sortedPeriods}
                        subTitle="(monthly)"
                        ChartTitle="Good Practice vs Issues Trend"
                      />
                    </View>
                  )}
                  {/* {filteredData.length > 0 && (
                    <View style={logStyles.card}>
                      <GroupBarChartsComp
                        legend={closeoutBarData.legend}
                        data={closeoutBarData}
                        label={closeoutBarData.labels}
                        subTitle="(% of observations closed per period)"
                        ChartTitle="Monthly Close-out Rate (%)"
                      />
                    </View>
                  )} */}
                  {/* {closedItems.length > 0 && (
                    <View style={logStyles.card}>
                      <GroupBarChartsComp
                        legend={avgDaysBarData.legend}
                        data={avgDaysBarData}
                        label={avgDaysBarData.labels}
                        subTitle="(closed observations only)"
                        ChartTitle="Average Days to Close by Category"
                      />
                    </View>
                  )} */}
                  {/* {filteredData.filter((i: any) => i.Contractor).length > 0 && (
                    <View style={logStyles.card}>
                      <PieChartComp
                        data={contractorPie}
                        colorPallate={interpolateBlues}
                        subTitle="(cumulative)"
                        ChartTitle="Observations by Contractor"
                      />
                    </View>
                  )} */}
                </View>
              </View>
            </ScrollView>
          </TabScreen>
          <TabScreen label="Tables" icon="table">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardTwo}>
                    {tableByType.length > 0 && (
                      <CustomDT
                        data={tableByType}
                        columns={colsByType}
                        siteId={siteId}
                        logTitle={logTitle}
                        tableId="ObservationsIssuesByType"
                        tableTitle="Environmental Observations/ Issues - By Type"
                      />
                    )}
                  </View>
                </View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardTwo}>
                    {tableByCategory.length > 0 && (
                      <CustomDT
                        data={tableByCategory}
                        columns={colsByCategory}
                        siteId={siteId}
                        logTitle={logTitle}
                        tableId="ObservationsIssuesByCategory"
                        tableTitle="Environmental Observations/ Issues - By Category"
                      />
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>
          <TabScreen label="List" icon="database">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    {overdueItems.length > 0 && (
                      <View style={{
                        backgroundColor: '#FFEBEE',
                        borderLeftWidth: 4,
                        borderLeftColor: '#B71C1C',
                        padding: 12,
                        marginBottom: 8,
                        borderRadius: 4,
                      }}>
                        <Text style={{ color: '#B71C1C', fontWeight: 'bold' }}>
                          {`⚠ ${overdueItems.length} observation${overdueItems.length > 1 ? 's' : ''} overdue`}
                        </Text>
                      </View>
                    )}
                    {filteredData && (
                      <PaperTableDynamic
                        data={filteredData}
                        columns={ColumnsWithIDColumn}
                        siteId={siteId}
                        logTitle={logTitle}
                        updateData={handleDataUpdate}
                        isConsolidatedTable={false}
                        Permissions={Permissions}
                        QueryStringID={itemID && itemID}
                        chartVisibility={chartVisibility}
                        funcSetShowFilter={setShowFilter}
                      />
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>
        </Tabs>
      </TabsProvider>
    </View>
  );
};
